import { Link } from 'react-router-dom';
import { LEGAL_EFFECTIVE_DATE, PUBLISHER } from './legal-facts';
import { LegalShell, List, Mail, Section } from './LegalShell';

/**
 * Fint's privacy policy.
 *
 * ── EVERY SENTENCE HERE IS A FACT ABOUT THE CODE ──
 * Written from what the app actually stores and sends, as of the effective
 * date - the tables in apps/api/src/database/schema.ts, the one cookie in
 * common/guards/auth.guard.ts, the outbound calls (Hostinger SMTP, Cloudflare
 * R2, Frankfurter), and the absence of analytics that index.html's CSP
 * enforces. A change that makes one of these sentences untrue - a new
 * processor, a tracker, a new kind of data, a longer retention - changes this
 * page in the same commit. docs/directory-listing.md lists what to recheck.
 *
 * ── DPDP ACT 2023 ──
 * Fint is two things at once. For the people who sign up, it decides why and
 * how their account data is used: a Data Fiduciary. For the people INSIDE a
 * book - a business's customers and vendors - the business decides, and Fint
 * processes on its behalf: a Data Processor. The policy says which is which,
 * because the rights requests go to different places.
 */
export function PrivacyPage() {
  return (
    <LegalShell title="Privacy policy" updated={LEGAL_EFFECTIVE_DATE}>
      <p>
        Fint keeps a business's books: invoices, expenses, bank accounts and GST. This policy explains what Fint collects,
        why, who else sees it, how long it is kept, and what you can ask us to do. It is written to be read, so where the
        plain answer is short, it is short.
      </p>

      <Section title="Who we are">
        <p>
          Fint is run by <strong>{PUBLISHER.name}</strong>, {PUBLISHER.address.join(', ')} (GSTIN {PUBLISHER.gstin}). In
          this policy, &ldquo;we&rdquo; means {PUBLISHER.name} and &ldquo;you&rdquo; means a person who uses Fint.
        </p>
        <p>
          For questions or requests about your data, write to <Mail />. Our Grievance Officer is{' '}
          <strong>{PUBLISHER.grievanceOfficer}</strong>, at the same address and email.
        </p>
      </Section>

      <Section title="Two kinds of data, two roles">
        <List>
          <li>
            <strong>Your account</strong>: your name, email and sign-in details. We decide how this is used, so under
            India&rsquo;s Digital Personal Data Protection Act, 2023 we are its <em>Data Fiduciary</em>.
          </li>
          <li>
            <strong>What is in your books</strong>: your business&rsquo;s records, including details of your customers,
            vendors and team. Your business decides what goes in and why; we store and process it only to run Fint for you.
            For this data we are a <em>Data Processor</em>, and your business is the Data Fiduciary. If one of your
            customers asks about their data, they should ask your business, and we will help you answer.
          </li>
        </List>
      </Section>

      <Section title="What we collect">
        <p>
          <strong>Your account.</strong> Name, email address, a one-way hash of your password (never the password
          itself), and, if you turn on two-step sign-in, its secret and hashed recovery codes. When you last signed in.
        </p>
        <p>
          <strong>Sign-in sessions.</strong> For each session: when it started and ends, the IP address it started from,
          and the browser&rsquo;s description of itself. We use these to show you where you are signed in and to spot
          misuse.
        </p>
        <p>
          <strong>Your books.</strong> Whatever you or your team enter, import or upload: your business&rsquo;s name,
          address, GSTIN and bank details for invoices; customers and vendors (names, contact details, addresses, GSTIN,
          PAN); items, quotes, invoices, payments, expenses, bank statement lines, projects, tags and notes; receipts and
          other files; questions you ask on the CFO page and the answers to them; reminders you send. Each book also keeps
          an activity log of who changed what, and when, because accounts need one.
        </p>
        <p>
          <strong>Email Fint sends for you.</strong> When you email an invoice or a reminder from Fint: who it went to, the
          subject, when, and whether it was delivered.
        </p>
        <p>
          <strong>Connected assistants.</strong> When you connect an AI assistant (see below): the name it gave itself,
          where its sign-in returns to, the IP address it registered from, which books you allowed, and when it was last
          used.
        </p>
        <p>
          <strong>What we do not collect.</strong> Fint has no analytics, no advertising trackers and no third-party
          scripts: the app loads nothing from anyone else&rsquo;s servers. We do not collect your location, your
          phone&rsquo;s contacts, or card numbers.
        </p>
      </Section>

      <Section title="Cookies and browser storage">
        <p>
          One cookie, <code>fint_session</code>, keeps you signed in. It cannot be read by scripts on the page and is sent
          only to Fint. Your browser also stores a few preferences on your own device, such as light or dark theme and
          which book you last opened. There are no tracking or advertising cookies, so there is nothing to opt out of.
        </p>
      </Section>

      <Section title="How we use it">
        <List>
          <li>To run Fint: show your books, work out balances and returns, produce documents, and keep them safe.</li>
          <li>To send the emails you ask Fint to send, and the ones the service needs (password resets, invitations).</li>
          <li>To keep Fint secure: limiting repeated attempts, investigating misuse, and protecting accounts.</li>
          <li>To answer you when you write to us.</li>
          <li>To meet a legal obligation, such as a lawful order from a court or a tax authority.</li>
        </List>
        <p>
          We do <strong>not</strong> sell your data, use it for advertising, or send you marketing email unless you have
          asked for it. We do <strong>not</strong> use your data to train AI models, and Fint itself sends your data to no
          AI model.
        </p>
      </Section>

      <Section title="AI assistants you connect">
        <p>
          You can connect an AI assistant, such as Claude or ChatGPT, to Fint. You choose which of your books it can see,
          on a screen that shows you what it is asking for. From then on it reads those books when you ask it something,
          and what it reads is processed by that assistant&rsquo;s provider under <em>their</em> terms and privacy policy,
          not ours. A question typed on Fint&rsquo;s CFO page waits until your connected assistant picks it up, and the
          assistant reads the figures it needs to answer it.
        </p>
        <p>
          A connected assistant can read, and it can <em>propose</em> records, such as a draft invoice or an expense from a
          bill. It cannot send anything to your customers, record or move money, or confirm its own proposals: a person
          does that in Fint. You can disconnect an assistant at any time in{' '}
          <strong>Settings &rarr; Connected assistants</strong>; it stops working on its next request. See{' '}
          <Link className="text-primary hover:underline" data-tap-exempt="text link in a sentence" to="/docs/mcp">
            Connect an assistant
          </Link>
          .
        </p>
      </Section>

      <Section title="Who else sees your data">
        <p>We use a small number of service providers, each only for what is listed here:</p>
        <List>
          <li>
            <strong>Hostinger</strong> hosts Fint&rsquo;s servers and database in India, and sends Fint&rsquo;s email.
          </li>
          <li>
            <strong>Cloudflare</strong> (R2 storage) holds the files you upload and the documents Fint produces. Cloudflare
            may store them outside India.
          </li>
          <li>
            <strong>Frankfurter</strong> supplies published exchange rates. Fint sends it only a currency and a date, never
            anything about you.
          </li>
          <li>
            <strong>Razorpay</strong>, once paid plans begin, will take payments for Fint. Your card or bank details go to
            Razorpay, not to us.
          </li>
        </List>
        <p>Beyond those, your data is seen by:</p>
        <List>
          <li>The people you invite into a book (your team, your accountant), with the role you give them.</li>
          <li>
            Your customers, when you send them a document. A shared invoice link opens that one invoice for anyone who has
            the link.
          </li>
          <li>Any assistant you connect, as described above.</li>
          <li>Authorities, only when the law requires us to hand data over.</li>
        </List>
      </Section>

      <Section title="Where it is stored">
        <p>
          The database is in India. Uploaded files are with Cloudflare, which may keep them in other countries. Any transfer
          outside India follows the DPDP Act and any restriction the Government of India places on it.
        </p>
      </Section>

      <Section title="How long we keep it">
        <List>
          <li>
            <strong>Your account and books</strong>: for as long as your account is open.
          </li>
          <li>
            <strong>When you delete your account</strong> (Settings &rarr; Your account): you have 30 days to change your
            mind or download your books. After that the account, and every book only you own, are deleted. A book that
            another owner shares stays with them; you are removed from it.
          </li>
          <li>
            <strong>Sign-in sessions</strong>: deleted 90 days after they end.
          </li>
        </List>
        <p>
          <strong>Your own record-keeping duty.</strong> Indian law requires a business to keep its accounts for years
          (eight under the Companies Act; six under GST). That duty is your business&rsquo;s. Download your books before you
          delete them.
        </p>
        <p>
          <strong>Backups.</strong> We do not keep separate backups of the database at present. Deleted data is gone, and a
          serious failure could lose data. Download your books regularly: Settings &rarr; Your account.
        </p>
      </Section>

      <Section title="How we protect it">
        <List>
          <li>Everything travels over HTTPS.</li>
          <li>Passwords are stored only as one-way hashes, and so are session tokens.</li>
          <li>Two-step sign-in is available on every account.</li>
          <li>Access is by role, book by book, and every change is logged with who made it.</li>
          <li>An assistant gets only the books you allowed, and you can cut it off at any time.</li>
        </List>
        <p>
          No system is perfectly secure. If a breach affects your personal data, we will tell you, and the Data Protection
          Board of India, within 72 hours of finding out, with what happened and what you can do.
        </p>
      </Section>

      <Section title="Your rights">
        <p>Under the DPDP Act you can:</p>
        <List>
          <li>ask what personal data we hold about you and how it is used;</li>
          <li>correct or update it (most of what is in your books you can change yourself in Fint);</li>
          <li>have it erased: delete your account in Settings, or write to us;</li>
          <li>nominate someone to act for you if you die or cannot act yourself;</li>
          <li>complain to our Grievance Officer, and if you are not satisfied, to the Data Protection Board of India.</li>
        </List>
        <p>
          Write to <Mail />. We reply within 30 days. If you are one of a Fint user&rsquo;s customers or vendors, please ask
          that business first: it decides what is in its books.
        </p>
      </Section>

      <Section title="Age">
        <p>Fint is for people aged 18 or over. We do not knowingly hold data about children.</p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          If we change what this policy promises, we will tell you by email or in Fint at least 30 days before the change
          takes effect, and update the date at the top. Smaller corrections take effect when published.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          {PUBLISHER.name}, {PUBLISHER.address.join(', ')}. Email <Mail />. Grievance Officer: {PUBLISHER.grievanceOfficer}.
        </p>
      </Section>
    </LegalShell>
  );
}
